import { VOICE_PARTS } from '@corocanto/scoring';
import { type FormEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  type AdminChoir,
  type DirectorRequest,
  useAdminChoirs,
  useAdminStatus,
  useAdminUsers,
  useChangeDirector,
  useCreateChoir,
  useRenameChoir,
  useReset,
  useResetPreview,
} from '../../api/admin';
import { Person } from '../../components/Avatar';
import { Dialog } from '../../components/Dialog';
import { ErrorView } from '../../components/Guards';
import { dateLocale, LANGUAGES } from '../../i18n';
import { formatDate } from '../../lib/format';
import ui from '../../styles/ui.module.css';
import { LinkBox } from './AdminUsersPage';
import { apiErrorText } from './adminFormat';
import styles from './AdminPages.module.css';

const DAY_MS = 24 * 60 * 60 * 1000;

function validDays(expiresAt: string | null): number {
  return expiresAt ? Math.max(1, Math.round((new Date(expiresAt).getTime() - Date.now()) / DAY_MS)) : 7;
}

/** The administrator's choirs (wireframe 13): the list, a new choir, and "Reset before launch" until go-live. */
export function AdminChoirsPage() {
  const { t } = useTranslation();
  const status = useAdminStatus();
  const choirs = useAdminChoirs();
  const [renaming, setRenaming] = useState<AdminChoir | null>(null);
  const [changing, setChanging] = useState<AdminChoir | null>(null);
  const [resetting, setResetting] = useState(false);
  const goLive = status.data?.goLive ?? true;
  const list = choirs.data ?? [];

  return (
    <div className={`${ui.page} ${styles.page}`}>
      <div className={ui.pageHeader}>
        <div>
          <h1>{t('admin.choirs.title')}</h1>
          {status.data && (
            <div className={ui.subtitle}>
              {t('admin.choirs.summary', { count: list.length })}
              {!goLive && <> · {t('admin.choirs.testPhase')}</>}
            </div>
          )}
        </div>
        {!goLive && (
          <button type="button" className={`${ui.button} ${styles.dangerOutline}`} onClick={() => setResetting(true)}>
            {t('admin.reset.open')}
          </button>
        )}
      </div>
      <div className={styles.choirsGrid}>
        <section className={ui.card}>
          {choirs.isError && <ErrorView onRetry={() => void choirs.refetch()} />}
          {choirs.isPending && <p className={ui.muted}>{t('common.loading')}</p>}
          {choirs.data && list.length === 0 && <p className={ui.muted}>{t('admin.choirs.none')}</p>}
          {list.length > 0 && (
            <table className={ui.table}>
              <thead>
                <tr>
                  <th>{t('admin.choirs.colChoir')}</th>
                  <th>{t('admin.choirs.colDirector')}</th>
                  <th>{t('admin.choirs.colMembers')}</th>
                  <th>{t('admin.choirs.colInvitations')}</th>
                  <th>{t('admin.choirs.colSongs')}</th>
                </tr>
              </thead>
              <tbody>
                {list.map((c) => (
                  <tr key={c.id} data-testid="admin-choir-row">
                    <td>
                      <div className={styles.choirName}>{c.name}</div>
                      <div className={ui.secondary}>{t('admin.choirs.created', { date: formatDate(c.createdAt, dateLocale()) })}</div>
                      <div className={styles.rowActions}>
                        <button type="button" className={ui.linkButton} onClick={() => setRenaming(c)}>
                          {t('admin.choirs.rename')}
                        </button>
                        <span aria-hidden="true">·</span>
                        <button type="button" className={ui.linkButton} onClick={() => setChanging(c)}>
                          {t('admin.choirs.changeDirector')}
                        </button>
                      </div>
                    </td>
                    <td>
                      {c.directors.length === 0 && <span className={ui.muted}>–</span>}
                      {c.directors.map((d) => (
                        <Person key={d.id} name={d.displayName} kind="director" size="small">
                          <span className={styles.director}>
                            {d.displayName}
                            {d.setupPending && <span className={ui.secondary}>{t('admin.choirs.setupPending')}</span>}
                          </span>
                        </Person>
                      ))}
                    </td>
                    <td className={styles.nowrap}>
                      {c.members === 0
                        ? 0
                        : [String(c.members), ...VOICE_PARTS.map((p) => `${t(`partLetters.${p}`)} ${c.byPart[p] ?? 0}`)].join(' · ')}
                    </td>
                    <td>{c.invitationsPending}</td>
                    <td>{c.songs}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
        <NewChoirCard />
      </div>
      {/* Keyed by choir, so each opening starts fresh. */}
      <RenameDialog key={`rename-${renaming?.id}`} choir={renaming} onClose={() => setRenaming(null)} />
      <ChangeDirectorDialog key={`director-${changing?.id}`} choir={changing} onClose={() => setChanging(null)} />
      {!goLive && <ResetDialog open={resetting} choirs={list} onClose={() => setResetting(false)} />}
    </div>
  );
}

/** The director: an existing user, or a new one who gets a setup link. */
function DirectorFields({ value, onChange }: { value: DirectorChoice; onChange: (value: DirectorChoice) => void }) {
  const { t } = useTranslation();
  const users = useAdminUsers('', '');
  return (
    <>
      <fieldset className={styles.radios}>
        <legend className={ui.label}>{t('admin.choirs.director')}</legend>
        <label className={styles.check}>
          <input type="radio" checked={value.existing} onChange={() => onChange({ ...value, existing: true })} />
          {t('admin.choirs.existingUser')}
        </label>
        <label className={styles.check}>
          <input type="radio" checked={!value.existing} onChange={() => onChange({ ...value, existing: false })} />
          {t('admin.choirs.newUser')}
        </label>
      </fieldset>
      {value.existing ? (
        <label className={ui.field}>
          <span className={ui.label}>{t('admin.choirs.user')}</span>
          <select className={ui.select} value={value.userId} onChange={(e) => onChange({ ...value, userId: e.target.value })}>
            <option value="">–</option>
            {(users.data ?? [])
              .filter((u) => u.status !== 'DEACTIVATED')
              .map((u) => (
                <option key={u.id} value={u.id}>
                  {u.displayName} · {u.email}
                </option>
              ))}
          </select>
        </label>
      ) : (
        <>
          <label className={ui.field}>
            <span className={ui.label}>{t('admin.choirs.directorName')}</span>
            <input className={ui.input} value={value.displayName} onChange={(e) => onChange({ ...value, displayName: e.target.value })} />
          </label>
          <label className={ui.field}>
            <span className={ui.label}>{t('admin.choirs.directorEmail')}</span>
            <input className={ui.input} type="email" value={value.email} onChange={(e) => onChange({ ...value, email: e.target.value })} />
          </label>
          <label className={ui.field}>
            <span className={ui.label}>{t('admin.panel.language')}</span>
            <select className={`${ui.select} ${styles.narrow}`} value={value.locale} onChange={(e) => onChange({ ...value, locale: e.target.value })}>
              {LANGUAGES.map((l) => (
                <option key={l} value={l}>
                  {t(`languages.${l}`)}
                </option>
              ))}
            </select>
          </label>
        </>
      )}
    </>
  );
}

interface DirectorChoice {
  existing: boolean;
  userId: string;
  displayName: string;
  email: string;
  locale: string;
}

const NO_DIRECTOR: DirectorChoice = { existing: false, userId: '', displayName: '', email: '', locale: 'tr' };

function directorRequest(d: DirectorChoice): DirectorRequest {
  return d.existing ? { userId: d.userId } : { displayName: d.displayName.trim(), email: d.email.trim(), locale: d.locale };
}

function directorComplete(d: DirectorChoice): boolean {
  return d.existing ? !!d.userId : !!d.displayName.trim() && !!d.email.trim();
}

function NewChoirCard() {
  const { t } = useTranslation();
  const create = useCreateChoir();
  const [name, setName] = useState('');
  const [director, setDirector] = useState<DirectorChoice>(NO_DIRECTOR);
  const [result, setResult] = useState<{ link: string; days: number; name: string } | null>(null);
  function submit(e: FormEvent) {
    e.preventDefault();
    const directorName = director.displayName.trim();
    create.mutate(
      { name: name.trim(), director: directorRequest(director) },
      {
        onSuccess: (r) => {
          setResult(r.link ? { link: r.link, days: validDays(r.linkExpiresAt), name: directorName } : null);
          setName('');
          setDirector(NO_DIRECTOR);
        },
      },
    );
  }
  return (
    <section className={ui.card} data-testid="new-choir">
      <form className={styles.form} onSubmit={submit} noValidate>
        <div>
          <h2 className={ui.cardTitle}>{t('admin.choirs.newTitle')}</h2>
          <p className={`${ui.muted} ${styles.noMargin}`}>{t('admin.choirs.newIntro')}</p>
        </div>
        <label className={ui.field}>
          <span className={ui.label}>{t('admin.choirs.name')}</span>
          <input className={ui.input} value={name} onChange={(e) => setName(e.target.value)} />
        </label>
        <DirectorFields value={director} onChange={setDirector} />
        {create.isError && <p className={styles.error}>{apiErrorText(create.error, t)}</p>}
        <div className={styles.buttonRow}>
          <button type="submit" className={`${ui.button} ${ui.primary}`} disabled={!name.trim() || !directorComplete(director) || create.isPending}>
            {t('admin.choirs.create')}
          </button>
        </div>
        {create.isSuccess && !result && (
          <p className={`${ui.notice} ${ui.noticeSuccess}`} role="status">
            {t('admin.choirs.createdExisting')}
          </p>
        )}
        {result && (
          <LinkBox
            link={result.link}
            title={t('admin.choirs.createdLink', { name: result.name, count: result.days })}
            hint={t('admin.choirs.linkHint')}
          />
        )}
      </form>
    </section>
  );
}

function RenameDialog({ choir, onClose }: { choir: AdminChoir | null; onClose: () => void }) {
  const { t } = useTranslation();
  const rename = useRenameChoir();
  const [name, setName] = useState(choir?.name ?? '');
  return (
    <Dialog open={!!choir} title={t('admin.choirs.renameTitle')} onClose={onClose} data-testid="rename-choir-dialog">
      <form
        className={styles.form}
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          if (choir) rename.mutate({ choirId: choir.id, name: name.trim() }, { onSuccess: onClose });
        }}
      >
        <label className={ui.field}>
          <span className={ui.label}>{t('admin.choirs.name')}</span>
          <input className={ui.input} value={name} onChange={(e) => setName(e.target.value)} />
        </label>
        {rename.isError && <p className={styles.error}>{apiErrorText(rename.error, t)}</p>}
        <div className={styles.buttonRow}>
          <button type="button" className={ui.button} onClick={onClose}>
            {t('common.cancel')}
          </button>
          <button type="submit" className={`${ui.button} ${ui.primary}`} disabled={!name.trim() || rename.isPending}>
            {t('admin.panel.save')}
          </button>
        </div>
      </form>
    </Dialog>
  );
}

function ChangeDirectorDialog({ choir, onClose }: { choir: AdminChoir | null; onClose: () => void }) {
  const { t } = useTranslation();
  const change = useChangeDirector();
  const [director, setDirector] = useState<DirectorChoice>({ ...NO_DIRECTOR, existing: true });
  const [link, setLink] = useState<{ link: string; days: number; name: string } | null>(null);
  const close = onClose;
  return (
    <Dialog open={!!choir} title={t('admin.choirs.changeDirectorTitle', { name: choir?.name ?? '' })} onClose={close} data-testid="change-director-dialog">
      {link ? (
        <div className={styles.form}>
          <LinkBox link={link.link} title={t('admin.choirs.directorLink', { name: link.name, count: link.days })} hint={t('admin.choirs.linkHint')} />
          <div className={styles.buttonRow}>
            <button type="button" className={`${ui.button} ${ui.primary}`} onClick={close}>
              {t('members.inviteDialog.done')}
            </button>
          </div>
        </div>
      ) : (
        <form
          className={styles.form}
          noValidate
          onSubmit={(e) => {
            e.preventDefault();
            if (!choir) return;
            const name = director.displayName.trim();
            change.mutate(
              { choirId: choir.id, director: directorRequest(director) },
              { onSuccess: (r) => (r.link ? setLink({ link: r.link, days: validDays(r.linkExpiresAt), name }) : close()) },
            );
          }}
        >
          <p className={`${ui.muted} ${styles.noMargin}`}>
            {t('admin.choirs.changeDirectorIntro', { names: choir?.directors.map((d) => d.displayName).join(', ') || '–' })}
          </p>
          <DirectorFields value={director} onChange={setDirector} />
          {change.isError && <p className={styles.error}>{apiErrorText(change.error, t)}</p>}
          <div className={styles.buttonRow}>
            <button type="button" className={ui.button} onClick={close}>
              {t('common.cancel')}
            </button>
            <button type="submit" className={`${ui.button} ${ui.primary}`} disabled={!directorComplete(director) || change.isPending}>
              {t('admin.choirs.changeDirector')}
            </button>
          </div>
        </form>
      )}
    </Dialog>
  );
}

/** "Reset before launch": tick the choirs that continue; the rest go, with every account left without a choir. */
function ResetDialog({ open, choirs, onClose }: { open: boolean; choirs: AdminChoir[]; onClose: () => void }) {
  const { t } = useTranslation();
  const [keep, setKeep] = useState<string[]>([]);
  const [typed, setTyped] = useState('');
  const preview = useResetPreview(keep, open);
  const reset = useReset();
  const word = 'RESET';
  function close() {
    setKeep([]);
    setTyped('');
    reset.reset();
    onClose();
  }
  const p = preview.data;
  return (
    <Dialog open={open} title={t('admin.reset.title')} onClose={close} wide data-testid="reset-dialog">
      {reset.isSuccess ? (
        <div className={styles.form}>
          <p className={`${ui.notice} ${ui.noticeSuccess}`} role="status">
            {t('admin.reset.done')}
          </p>
          <p className={ui.muted}>{t('admin.reset.afterwards')}</p>
          <div className={styles.buttonRow}>
            <button type="button" className={`${ui.button} ${ui.primary}`} onClick={close}>
              {t('members.inviteDialog.done')}
            </button>
          </div>
        </div>
      ) : (
        <div className={styles.form}>
          <p className={styles.noMargin}>{t('admin.reset.intro')}</p>
          <div>
            <span className={ui.label}>{t('admin.reset.tick')}</span>
            <ul className={styles.resetList}>
              {choirs.map((c) => {
                const kept = keep.includes(c.id);
                return (
                  <li key={c.id}>
                    <label className={styles.check}>
                      <input
                        type="checkbox"
                        checked={kept}
                        onChange={(e) => setKeep(e.target.checked ? [...keep, c.id] : keep.filter((id) => id !== c.id))}
                      />
                      <strong>{c.name}</strong>
                      <span className={ui.muted}>
                        · {c.members === 0 ? t('admin.reset.noMembers') : t('admin.reset.choirFacts', { members: c.members, songs: c.songs })}
                      </span>
                    </label>
                    <span className={kept ? styles.keepTag : styles.deleteTag}>{kept ? t('admin.reset.keep') : t('admin.reset.delete')}</span>
                  </li>
                );
              })}
            </ul>
          </div>
          {p && (
            <div className={styles.resetBoxes} data-testid="reset-preview">
              <div className={styles.deletedBox}>
                <strong>{t('admin.reset.deleted')}</strong>
                <p>
                  {t('admin.reset.deletedFacts', {
                    choirs: p.deleted.choirs,
                    accounts: p.deletedAccounts,
                    songs: p.deleted.songs,
                    events: p.deleted.events,
                    submissions: p.deleted.submissions,
                    grades: p.deleted.grades,
                    comments: p.deleted.comments,
                  })}
                </p>
              </div>
              <div className={styles.keptBox}>
                <strong>{t('admin.reset.kept')}</strong>
                <p>{t('admin.reset.keptFacts', { choirs: p.keptChoirs, accounts: p.keptAccounts })}</p>
              </div>
            </div>
          )}
          {p?.keptButLose.map((k) => (
            <p key={k.displayName} className={`${ui.muted} ${styles.noMargin}`}>
              {t('admin.reset.keptButLoses', { name: k.displayName, choirs: k.choirs })}
            </p>
          ))}
          <label className={ui.field}>
            <span className={ui.label}>{t('admin.reset.type', { word })}</span>
            <input className={ui.input} value={typed} onChange={(e) => setTyped(e.target.value)} data-testid="reset-confirm" />
          </label>
          {reset.isError && <p className={styles.error}>{apiErrorText(reset.error, t)}</p>}
          <div className={styles.buttonRow}>
            <button type="button" className={ui.button} onClick={close}>
              {t('common.cancel')}
            </button>
            <button
              type="button"
              className={`${ui.button} ${styles.danger}`}
              disabled={typed.trim() !== word || reset.isPending}
              onClick={() => reset.mutate({ keep, confirm: 'RESET' })}
            >
              {t('admin.reset.confirm')}
            </button>
          </div>
          <p className={styles.footnote}>{t('admin.reset.afterwards')}</p>
        </div>
      )}
    </Dialog>
  );
}
